import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  FaCode, FaCloud, FaCogs, FaChartLine, FaDatabase, FaShieldAlt, 
  FaSyncAlt, FaLaptopCode, FaChevronDown, FaCheckCircle, 
  FaGlobe, FaRocket, FaTools, FaMicrochip 
} from "react-icons/fa";

import netsuiteImg from "../assets/images/netsuite.jpg";
import "../styles/NetSuite.css";

const nsFaqs = [
  {
    q: "How do you handle NetSuite Governor Limits in complex scripts?",
    a: "We utilize SuiteScript 2.1's asynchronous processing and Map/Reduce scripts to handle large datasets. By architecting scripts to yield or reschedule when usage limits are approached, we ensure 100% execution success for enterprise-scale processing."
  },
  {
    q: "Can you implement NetSuite OneWorld for global subsidiaries?",
    a: "Yes. Our architects specialize in OneWorld configurations, including automated intercompany eliminations, multi-currency revaluation, and global tax compliance (SuiteTax) across dozens of international legal entities."
  },
  {
    q: "What is your approach to NetSuite-to-Salesforce integration?",
    a: "We build robust bridges using SuiteTalk REST Web Services or middleware like Celigo. We focus on real-time data synchronization for the Lead-to-Cash cycle, ensuring financial data in NetSuite matches CRM activity in Salesforce perfectly."
  }
];

export default function NetSuite() {
  const [activeFaq, setActiveFaq] = useState(null);

  return (
    <div className="ns-page animated-bg">
      <Helmet>
        <title>NetSuite Consultant & SuiteScript 2.1 Developer | ITSeva Hub</title>
        <meta name="description" content="Certified NetSuite Architects specializing in SuiteScript 2.1, OneWorld global ERP, and automated financial cycles. Optimize your business with ITSeva Hub." />
        <meta name="keywords" content="NetSuite Consultant, SuiteScript 2.1 Developer, NetSuite OneWorld, ERP Automation, SuiteTalk REST API, NetSuite Integration" />
      </Helmet>

      <div className="animated-mesh"></div>

      {/* --- HERO SECTION --- */}
      <section className="ns-hero">
        <div className="ns-container">
          <div className="ns-hero-grid">
            <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
              <div className="badge-modern ns-badge"><FaCloud /> Oracle NetSuite Specialized</div>
              <h1>Global ERP <br/> <span className="text-gradient">Optimization Core</span></h1>
              <p className="ns-description">
                Building the "Financial Brain" for modern enterprises. From <strong>SuiteScript 2.1</strong> performance 
                tuning to <strong>OneWorld global consolidation</strong>, we engineer NetSuite environments that scale without limits.
              </p>
              <div className="ns-hero-btns">
                <Link to="/contact" className="ns-btn-primary ns-btn">Request Technical Audit</Link>
                <div className="hero-stat"><strong>50+</strong> <span>Live Implementations</span></div>
              </div>
            </motion.div>

            <motion.div className="ns-hero-viz" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}>
              <div className="viz-glass">
                <img src={netsuiteImg} alt="NetSuite Enterprise Architecture" className="ns-main-img" />
                <motion.div animate={{ y: [0, -15, 0] }} transition={{ duration: 3, repeat: Infinity }} className="floating-tag ns-t1">SuiteScript 2.1</motion.div>
                <motion.div animate={{ y: [0, 15, 0] }} transition={{ duration: 4, repeat: Infinity }} className="floating-tag ns-t2">OneWorld Global</motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* --- STRATEGIC PILLARS --- */}
      <section className="ns-pillars">
        <div className="ns-container">
          <div className="pillars-grid">
            <div className="pillar-item glass">
              <FaSyncAlt />
              <h3>Velocity</h3>
              <p>Automating Order-to-Cash (O2C) and Procure-to-Pay (P2P) cycles to reduce manual data entry by 90%.</p>
            </div>
            <div className="pillar-item glass">
              <FaShieldAlt />
              <h3>Integrity</h3>
              <p>Ensuring SOC1/SOC2 compliance through rigorous script auditing and immutable data architecture.</p>
            </div>
            <div className="pillar-item glass">
              <FaChartLine />
              <h3>Visibility</h3>
              <p>Custom SuiteAnalytics workbooks providing real-time financial KPIs for global subsidiaries.</p>
            </div>
          </div>
        </div>
      </section>

      {/* --- CORE CAPABILITIES (BENTO) --- */}
      <section className="ns-services">
        <div className="ns-container">
          <div className="section-title center">
            <span className="accent-text">Expertise</span>
            <h2>ERP Engineering Layers</h2>
          </div>

          <div className="bento-grid">
            <motion.div whileHover={{ y: -5 }} className="bento-card glass tall">
              <FaLaptopCode className="b-icon ns-blue" />
              <h3>SuiteScript 2.1 Dev</h3>
              <p>Advanced development using <strong>ES6+</strong> standards for Map/Reduce, Scheduled, and RESTlet scripts.</p>
              <ul className="bento-list">
                <li><FaCheckCircle /> Governor Limit Management</li>
                <li><FaCheckCircle /> Modular Script Architecture</li>
                <li><FaCheckCircle /> SDF Deployment Pipelines</li>
              </ul>
            </motion.div>

            <div className="bento-right-stack">
              <motion.div whileHover={{ y: -5 }} className="bento-card glass wide">
                <FaGlobe className="b-icon ns-purple" />
                <div>
                  <h3>Global OneWorld Architecture</h3>
                  <p>Mastering multi-subsidiary financial consolidation, intercompany eliminations, and complex multi-currency revaluation across global markets.</p>
                </div>
              </motion.div>

              <div className="bento-inner-grid">
                <motion.div whileHover={{ y: -5 }} className="bento-card glass">
                  <FaCogs className="b-icon ns-pink" />
                  <h3>API Integrations</h3>
                  <p>SuiteTalk REST/SOAP bridges for Salesforce, Shopify, and 3PLs.</p>
                </motion.div>
                <motion.div whileHover={{ y: -5 }} className="bento-card glass">
                  <FaDatabase className="b-icon ns-orange" />
                  <h3>Data Migration</h3>
                  <p>Safe extraction and ETL from legacy systems (SAP, Sage, QuickBooks).</p>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- TECHNICAL DEEP DIVE (SEO BLOCK) --- */}
      <section className="ns-deep-dive">
        <div className="ns-container">
          <div className="deep-dive-grid glass">
            <div className="dive-text">
              <h2>Enterprise ERP Governance</h2>
              <p>
                Ranking as a leading <strong>NetSuite Consultant</strong> requires deep technical fluency in the <strong>SuiteCloud Platform</strong>. 
                We don't just "configure" settings; we architect systems. Our use of the <strong>SuiteCloud Development Framework (SDF)</strong> 
                allows for version-controlled, XML-based deployments that ensure staging and production environments stay in sync.
              </p>
              <p>
                Whether your business requires <strong>Advanced Revenue Management (ARM)</strong> or complex <strong>Inventory Distribution</strong>, 
                our team ensures that every customization is "Upgrade-Proof," following NetSuite’s best practices to prevent broken scripts during bi-annual updates.
              </p>
              <div className="tech-tags">
                <span>SuiteTalk</span> <span>SuiteAnalytics</span> <span>SuiteTax</span> <span>SDF</span> <span>RESTlets</span>
              </div>
            </div>
            <div className="dive-stats">
              <div className="d-stat"><h4>100%</h4><p>Certified Team</p></div>
              <div className="d-stat"><h4>0</h4><p>Critical Downtime</p></div>
              <div className="d-stat"><h4>ES6</h4><p>Script Standard</p></div>
            </div>
          </div>
        </div>
      </section>

      {/* --- METHODOLOGY --- */}
      <section className="ns-lifecycle">
        <div className="ns-container">
          <div className="section-title center">
            <span className="accent-text">The Process</span>
            <h2>The Implementation Roadmap</h2>
          </div>
          <div className="lifecycle-grid">
            {[
              { icon: <FaTools />, title: "Audit", desc: "Technical health check of existing scripts and data integrity." },
              { icon: <FaMicrochip />, title: "Architecture", desc: "ERD design and workflow mapping for financial cycles." },
              { icon: <FaCode />, title: "Engineering", desc: "Agile SuiteScript 2.1 development with modular testing." },
              { icon: <FaRocket />, title: "Go-Live", desc: "Controlled data migration and post-production hyper-care." }
            ].map((step, idx) => (
              <div className="step-card" key={idx}>
                <div className="step-num">{idx + 1}</div>
                {step.icon}
                <h4>{step.title}</h4>
                <p>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- FAQ SECTION --- */}
      <section className="ns-faq">
        <div className="ns-container narrow">
          <h2 className="center">NetSuite Technical Insights</h2>
          <div className="faq-list">
            {nsFaqs.map((faq, index) => (
              <div className={`faq-item glass ${activeFaq === index ? "active" : ""}`} key={index}>
                <button onClick={() => setActiveFaq(activeFaq === index ? null : index)}>
                  {faq.q} <FaChevronDown className={activeFaq === index ? "rotate" : ""} />
                </button>
                <AnimatePresence>
                  {activeFaq === index && (
                    <motion.div className="faq-answer" initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }}>
                      <p>{faq.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="ns-cta">
        <div className="ns-container center">
          <div className="cta-glass">
            <h2>Optimize Your Global ERP Core</h2>
            <p>Work with architects who bridge the gap between finance and code.</p>
            <Link to="/contact" className="btn-main-glow ns-glow">Book Architecture Call</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}